import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { BlueprintGrid } from "../components/BlueprintGrid";
import { fadeUp, viewportOnce, stagger } from "../lib/motion";
import { INSIGHTS, COMING_SOON } from "../lib/insights";

const InsightsPage = () => (
    <div className="min-h-screen bg-ink text-white">
        <Seo
            title="BuildEase Insights — Construction Guidance for SA Homeowners"
            description="Plain-language guidance on construction costs, Bills of Quantities, contractor quotes and building a home in South Africa — from the BuildEase team."
        />
        <Navbar />
        <main>
            <section className="relative overflow-hidden bg-ink pt-[128px] lg:pt-[168px]">
                <BlueprintGrid />
                <div className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-10 lg:px-16">
                    <motion.div variants={fadeUp} initial="hidden" animate="show">
                        <p className="flex items-center gap-3 font-mono text-[13px] tracking-[0.3em] text-mist">
                            <span className="h-1.5 w-1.5 bg-accent" />
                            BUILDEASE INSIGHTS
                        </p>
                        <h1
                            data-testid="insights-headline"
                            className="mt-8 max-w-4xl font-display text-4xl font-semibold leading-[1.08] text-white md:text-6xl"
                        >
                            Useful construction knowledge, in plain language
                            <span className="text-accent">.</span>
                        </h1>
                        <p className="mt-6 max-w-2xl text-[16px] leading-relaxed text-mist">
                            Practical guidance for South African homeowners —
                            written to be understood, with the assumptions
                            shown.
                        </p>
                    </motion.div>
                </div>
            </section>

            <section className="relative py-16 lg:py-24">
                <div className="mx-auto max-w-[1600px] px-6 md:px-10 lg:px-16">
                    <motion.div
                        variants={stagger}
                        initial="hidden"
                        whileInView="show"
                        viewport={viewportOnce}
                        className="grid gap-6 md:grid-cols-3"
                    >
                        {INSIGHTS.map((a) => (
                            <motion.article
                                key={a.slug}
                                variants={fadeUp}
                                data-testid={`insight-card-${a.slug}`}
                                className="group flex flex-col border border-white/12 bg-surface/80"
                            >
                                <div className="overflow-hidden">
                                    <img
                                        src={a.image}
                                        alt={a.alt}
                                        loading="lazy"
                                        className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                                    />
                                </div>
                                <div className="flex flex-1 flex-col px-6 py-6">
                                    <p className="flex items-center gap-3 font-mono text-[13px] tracking-[0.22em] text-cad">
                                        <span className="text-accent">{a.readTime}</span>
                                        · {a.date}
                                    </p>
                                    <h2 className="mt-4 font-display text-xl font-semibold leading-snug text-white">
                                        {a.title}
                                    </h2>
                                    <p className="mt-3 flex-1 text-[14px] leading-relaxed text-mist">
                                        {a.excerpt}
                                    </p>
                                    <Link
                                        to={`/insights/${a.slug}`}
                                        data-testid={`insight-read-${a.slug}`}
                                        className="mt-5 inline-flex items-center gap-1.5 text-[14px] font-semibold text-accent transition-colors duration-300 hover:text-[#ff7026]"
                                    >
                                        Read article
                                        <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                    </Link>
                                </div>
                            </motion.article>
                        ))}
                    </motion.div>

                    <motion.div
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="show"
                        viewport={viewportOnce}
                        data-testid="insights-coming-soon"
                        className="mt-16 border-t border-white/10 pt-10"
                    >
                        <p className="font-mono text-[13px] tracking-[0.3em] text-mist">
                            COMING SOON
                        </p>
                        <ul className="mt-6 grid gap-2.5 md:grid-cols-2 lg:grid-cols-3">
                            {COMING_SOON.map((t) => (
                                <li
                                    key={t}
                                    className="flex items-start gap-2.5 text-[15px] leading-relaxed text-mist/75"
                                >
                                    <span className="mt-2.5 h-1.5 w-1.5 shrink-0 bg-cad" />
                                    {t}
                                </li>
                            ))}
                        </ul>
                    </motion.div>
                </div>
            </section>
        </main>
        <Footer />
    </div>
);

export default InsightsPage;
